// Client reads, the creation mutation and the KYC control mutation, always under RLS
// with the caller session.
import type { Client, StatutKyc } from '@horizon/types';
import { getClient } from './client.js';
import { toErreur, withRetry } from './erreurs.js';

const COLONNES =
  'id,organisation_id,civilite,prenom,nom,date_naissance,date_entree_relation,' +
  'patrimoine_estime,objectif_annee,profil_investisseur,note_risque,statut_kyc,' +
  'kyc_valide_jusqu_au,kyc_commentaire,archive,cree_le,modifie_le';

export interface PageClients {
  readonly lignes: readonly Client[];
  readonly total: number;
}

/** Paginated client list ordered by name; `total` feeds the "i-j sur N" counter. */
export async function listerClients(page = 0, taille = 25): Promise<PageClients> {
  return withRetry(async () => {
    const debut = page * taille;
    const { data, error, count } = await getClient()
      .from('clients')
      .select(COLONNES, { count: 'exact' })
      .eq('archive', false)
      .order('nom', { ascending: true })
      .range(debut, debut + taille - 1);
    if (error !== null) {
      throw toErreur(error);
    }
    return { lignes: (data ?? []) as unknown as Client[], total: count ?? 0 };
  });
}

/**
 * Full assigned-client list for the clients screen. The screen filters (search,
 * profile, KYC) and paginates locally, exactly like the reference prototype, so
 * it needs every row of the caller perimeter at once. Rows keep the corpus
 * order: creation timestamp then identifier, both stable in the seed.
 */
export async function listerTousLesClients(): Promise<readonly Client[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('clients')
      .select(COLONNES)
      .eq('archive', false)
      .order('cree_le', { ascending: true })
      .order('id', { ascending: true });
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as Client[];
  });
}

/** Single client by identifier; null when it is out of the caller perimeter (RLS). */
export async function obtenirClient(id: string): Promise<Client | null> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('clients')
      .select(COLONNES)
      .eq('id', id)
      .maybeSingle();
    if (error !== null) {
      throw toErreur(error);
    }
    return (data as unknown as Client) ?? null;
  });
}

/** Number of non archived clients of the caller perimeter (dashboard counter). */
export async function compterClientsActifs(): Promise<number> {
  return withRetry(async () => {
    const { error, count } = await getClient()
      .from('clients')
      .select('id', { count: 'exact', head: true })
      .eq('archive', false);
    if (error !== null) {
      throw toErreur(error);
    }
    return count ?? 0;
  });
}

/** Clients whose KYC must be renewed, closest expiry first (RG-14, dashboard list). */
export async function clientsKycARenouveler(): Promise<readonly Client[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('clients')
      .select(COLONNES)
      .eq('archive', false)
      .eq('statut_kyc', 'a_renouveler')
      .order('kyc_valide_jusqu_au', { ascending: true });
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as Client[];
  });
}

/** Advisor to client assignment, for the advisor column of the conformité KYC queue. */
export interface Affectation {
  readonly conseiller_id: string;
  readonly client_id: string;
}

/**
 * Row inserted by the client creation form. Identifiers, timestamps and the archive
 * flag are never user input: `archive` is forced to false by the writer below, the two
 * timestamps are database defaults, and the organisation comes from the caller profile.
 */
export interface NouveauClient {
  readonly organisation_id: string;
  readonly civilite: string;
  readonly prenom: string;
  readonly nom: string;
  /** Calendar date AAAA-MM-JJ. */
  readonly date_naissance: string;
  /** Calendar date AAAA-MM-JJ. */
  readonly date_entree_relation: string;
  readonly patrimoine_estime: number;
  readonly objectif_annee: number;
  readonly profil_investisseur: string;
  readonly note_risque: number;
  readonly statut_kyc: StatutKyc;
  /** Calendar date AAAA-MM-JJ, or null when no control has been recorded yet. */
  readonly kyc_valide_jusqu_au: string | null;
}

/**
 * RFC 4122 version 4 identifier drawn by the caller. The creation insert carries it
 * explicitly so the statement needs no RETURNING clause: an advisor is not yet assigned
 * to the row they are creating, so clients_select would reject the returned row.
 */
function nouvelIdentifiantClient(): string {
  const source = globalThis.crypto;
  if (typeof source.randomUUID === 'function') {
    return source.randomUUID();
  }
  const octets = source.getRandomValues(new Uint8Array(16));
  octets.set([((octets.at(6) ?? 0) & 0x0f) | 0x40], 6);
  octets.set([((octets.at(8) ?? 0) & 0x3f) | 0x80], 8);
  const hexa = Array.from(octets, (octet) => octet.toString(16).padStart(2, '0')).join('');
  return [
    hexa.slice(0, 8),
    hexa.slice(8, 12),
    hexa.slice(12, 16),
    hexa.slice(16, 20),
    hexa.slice(20),
  ].join('-');
}

/**
 * Create a client in the caller organisation and return its identifier. Allowed to
 * administrateur and conseiller by the clients_insert policy; the audit_log row is
 * written by the database trigger, never by the application (RG-15). No retry: a
 * mutation is only sent once.
 */
export async function creerClient(entree: NouveauClient): Promise<string> {
  const id = nouvelIdentifiantClient();
  const { error } = await getClient()
    .from('clients')
    .insert({ ...entree, id, archive: false });
  if (error !== null) {
    throw toErreur(error);
  }
  return id;
}

/**
 * Attach an advisor to a client. An advisor who just created a client attaches
 * themselves, which is what makes the row visible to them afterwards
 * (fn_client_accessible). No retry, for the same reason as every other mutation.
 */
export async function affecterClient(clientId: string, conseillerId: string): Promise<void> {
  const { error } = await getClient()
    .from('affectations')
    .insert({ client_id: clientId, conseiller_id: conseillerId });
  if (error !== null) {
    throw toErreur(error);
  }
}

/** One line refused by the database, with the reason to show in the import report. */
export interface RejetImportClient {
  /** Zero based position of the line in the submitted batch. */
  readonly index: number;
  /** User facing French sentence, taken from the normalised error. */
  readonly motif: string;
}

export interface RapportImportClients {
  /** Identifiers actually written, in submission order. */
  readonly identifiantsCrees: readonly string[];
  readonly rejets: readonly RejetImportClient[];
}

/** Rows per statement. A refused batch is replayed line by line, so it stays small. */
const TAILLE_LOT_IMPORT = 50;

interface LigneImport {
  readonly index: number;
  readonly id: string;
  readonly valeurs: NouveauClient;
}

/**
 * One insert statement for a whole batch. No RETURNING clause and no retry, for the same
 * two reasons as creerClient: clients_select would reject rows the caller is not affected
 * to yet, and a mutation is only ever sent once.
 */
async function insererLotClients(lot: readonly LigneImport[]): Promise<void> {
  const { error } = await getClient()
    .from('clients')
    .insert(lot.map((ligne) => ({ ...ligne.valeurs, id: ligne.id, archive: false })));
  if (error !== null) {
    throw toErreur(error);
  }
}

/** Replays a refused batch one row at a time, so the report names the offending lines. */
async function insererUneAUne(lot: readonly LigneImport[]): Promise<RapportImportClients> {
  const identifiantsCrees: string[] = [];
  const rejets: RejetImportClient[] = [];
  for (const ligne of lot) {
    try {
      await insererLotClients([ligne]);
      identifiantsCrees.push(ligne.id);
    } catch (cause) {
      rejets.push({ index: ligne.index, motif: toErreur(cause).messageUtilisateur });
    }
  }
  return { identifiantsCrees, rejets };
}

/** Fast path first; the slow path only pays for itself when the batch is refused. */
async function insererLotOuRejouer(lot: readonly LigneImport[]): Promise<RapportImportClients> {
  try {
    await insererLotClients(lot);
    return { identifiantsCrees: lot.map((ligne) => ligne.id), rejets: [] };
  } catch {
    return insererUneAUne(lot);
  }
}

/**
 * Attach the caller advisor to every imported client, in one statement. Without it a
 * conseiller would not see the rows they just wrote (fn_client_accessible); an
 * administrateur already sees the whole organisation and passes no identifier.
 */
async function affecterClients(clientIds: readonly string[], conseillerId: string): Promise<void> {
  const { error } = await getClient()
    .from('affectations')
    .insert(clientIds.map((client) => ({ client_id: client, conseiller_id: conseillerId })));
  if (error !== null) {
    throw toErreur(error);
  }
}

/**
 * Batch client creation behind the CSV import. Rows are written by groups of
 * TAILLE_LOT_IMPORT; a group refused by a constraint or by the clients_insert policy is
 * replayed row by row so the advisor gets a per line reason instead of a blank failure.
 * The caller has already validated every row against the shared client schema: what is
 * caught here is what only the database can decide. The audit_log rows are written by the
 * database trigger, never by the application (RG-15).
 */
export async function importerClientsEnLot(
  entrees: readonly NouveauClient[],
  conseillerId: string | null = null,
): Promise<RapportImportClients> {
  const lignes: readonly LigneImport[] = entrees.map((valeurs, index) => ({
    index,
    id: nouvelIdentifiantClient(),
    valeurs,
  }));
  const identifiantsCrees: string[] = [];
  const rejets: RejetImportClient[] = [];
  for (let debut = 0; debut < lignes.length; debut += TAILLE_LOT_IMPORT) {
    const rapport = await insererLotOuRejouer(lignes.slice(debut, debut + TAILLE_LOT_IMPORT));
    identifiantsCrees.push(...rapport.identifiantsCrees);
    rejets.push(...rapport.rejets);
  }
  if (conseillerId !== null && identifiantsCrees.length > 0) {
    await affecterClients(identifiantsCrees, conseillerId);
  }
  return { identifiantsCrees, rejets };
}

/** Every assignment visible in the caller organisation (RLS). */
export async function listerAffectations(): Promise<readonly Affectation[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('affectations')
      .select('conseiller_id,client_id');
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as Affectation[];
  });
}

/** Renewal captured by the conformité modal, before it is sent to the database. */
export interface RenouvellementKyc {
  /** New validity end, ISO date (AAAA-MM-JJ), as typed in the date field. */
  readonly valideJusquAu: string;
  /** Free comment of the operator; a blank comment is stored as null. */
  readonly commentaire: string;
}

/**
 * Renew a KYC file: the status goes back to a_jour with a new validity end and the
 * comment of the control. Allowed by the clients_update policy to the administrateur,
 * the affected conseiller and the conformité; the audit_log row is written by the
 * database trigger, never by the application (RG-15). No retry: a mutation is only
 * sent once, so a timeout never renews twice.
 */
export async function renouvelerKyc(id: string, saisie: RenouvellementKyc): Promise<Client> {
  const commentaire = saisie.commentaire.trim();
  const { data, error } = await getClient()
    .from('clients')
    .update({
      statut_kyc: 'a_jour',
      kyc_valide_jusqu_au: saisie.valideJusquAu,
      kyc_commentaire: commentaire === '' ? null : commentaire,
    })
    .eq('id', id)
    .select(COLONNES)
    .single();
  if (error !== null) {
    throw toErreur(error);
  }
  return data as unknown as Client;
}

/**
 * Previous entry point of the KYC control, kept while index.ts still exports it.
 * New call sites use renouvelerKyc, which also carries the control comment.
 */
export async function marquerControleKyc(id: string, valideJusquAu: string): Promise<Client> {
  return renouvelerKyc(id, { valideJusquAu, commentaire: '' });
}

/** Global search: accent and case insensitive, from 2 characters, 8 results (EF-011). */
export async function rechercherClients(terme: string): Promise<readonly Client[]> {
  const requete = terme.trim();
  if (requete.length < 2) {
    return [];
  }
  return withRetry(async () => {
    const motif = `%${requete}%`;
    const { data, error } = await getClient()
      .from('clients')
      .select(COLONNES)
      .eq('archive', false)
      .or(`nom.ilike.${motif},prenom.ilike.${motif}`)
      .order('nom', { ascending: true })
      .limit(8);
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as Client[];
  });
}
